
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader, Upload, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { extractTextFromFile } from '@/lib/services/fileService';

interface FactSheetUploadProps {
  factSheetText: string | undefined;
  onSaveFactSheet: (text: string) => Promise<void>;
  isSaving: boolean;
}

const FactSheetUpload = ({ factSheetText, onSaveFactSheet, isSaving }: FactSheetUploadProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState<string>(factSheetText || '');
  
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if the file type is supported
    if (file.type !== 'application/pdf' && file.type !== 'text/plain') {
      toast.error('Unsupported file type. Please upload a PDF or TXT file.');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const text = await extractTextFromFile(file);
      setExtractedText(text);
      toast.success('File content extracted successfully!');
    } catch (error) {
      console.error('Error extracting text:', error);
      toast.error('Failed to extract text from file');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleSave = async () => {
    if (!extractedText.trim()) {
      toast.error('No text to save. Please upload a file first.');
      return;
    }
    
    await onSaveFactSheet(extractedText);
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Hotel Fact Sheet</CardTitle>
        <CardDescription>
          Upload your hotel's fact sheet to provide detailed information to guests through the AI assistant
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="factSheet">Upload Fact Sheet (PDF or TXT)</Label>
          <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
            <input
              type="file"
              id="factSheet"
              accept=".pdf,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Label 
              htmlFor="factSheet" 
              className="cursor-pointer flex flex-col items-center justify-center gap-2"
            >
              <Upload className="h-8 w-8 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">
                {isProcessing ? 'Processing file...' : 'Click to upload or drag and drop'}
              </span>
              <span className="text-xs text-gray-500">PDF or TXT files only</span>
            </Label>
            
            {isProcessing && (
              <div className="mt-4 flex items-center justify-center">
                <Loader className="h-5 w-5 animate-spin text-hotel-primary mr-2" />
                <span className="text-sm text-gray-600">Extracting text content...</span>
              </div>
            )}
          </div>
        </div>
        
        {extractedText && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-hotel-primary" />
                <Label>Extracted Content</Label>
              </div>
              <Button 
                onClick={handleSave}
                className="bg-hotel-primary hover:bg-hotel-primary/90"
                disabled={isSaving || isProcessing}
                size="sm"
              >
                {isSaving ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </div>
            
            <div className="border rounded-md p-3 bg-gray-50 max-h-72 overflow-y-auto">
              <pre className="text-sm whitespace-pre-wrap font-sans">
                {extractedText}
              </pre>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FactSheetUpload;
